import { extendTheme } from '@chakra-ui/react';
import colors from './foundations/colors';
import Card from './components/card';
import Button from './components/button';
import Table from './components/table';
import Modal from './components/modal';
import Input from './components/input';
import Select from './components/select';
import AlertDialog from './components/alert-dialog';
import Tabs from './components/tabs';
import Textarea from './components/textarea';
import Checkbox from './components/checkbox';
import Popover from './components/popover';
import Radio from './components/radio';
import Progress from './components/progress';

const theme = extendTheme({
  // 1. Colors
  colors,

  // 2. Typography - Larger, more professional
  fonts: {
    body: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    heading: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: '"SF Mono", "Consolas", "Liberation Mono", Menlo, monospace',
  },
  fontSizes: {
    xs: "0.8125rem",   // 13px
    sm: "0.9375rem",   // 15px
    md: "1.0625rem",   // 17px
    lg: "1.1875rem",   // 19px
    xl: "1.375rem",    // 22px
    "2xl": "1.625rem", // 26px
    "3xl": "2rem",     // 32px
    "4xl": "2.5rem",   // 40px
  },
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  lineHeights: {
    normal: 1.6,
    short: 1.4,
    shorter: 1.2,
    tall: 1.8,
  },

  // 3. Responsive Breakpoints
  breakpoints: {
    sm: '30em',   // 480px
    md: '48em',   // 768px
    lg: '62em',   // 992px
    xl: '80em',   // 1280px
    '2xl': '96em', // 1536px
  },

  // 4. Spacing Scale
  space: {
    px: '1px',
    0.5: '0.125rem',
    1: '0.25rem',
    1.5: '0.375rem',
    2: '0.5rem',
    2.5: '0.625rem',
    3: '0.75rem',
    3.5: '0.875rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    9: '2.25rem',
    10: '2.5rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    28: '7rem',
    32: '8rem',
    36: '9rem',
    40: '10rem',
    44: '11rem',
    48: '12rem',
    52: '13rem',
    56: '14rem',
    60: '15rem',
    64: '16rem',
    72: '18rem',
    80: '20rem',
    96: '24rem',
  },

  // 5. Border Radius - Slightly sharper for professional look
  radii: {
    none: '0',
    sm: '0.1875rem',  // 3px
    base: '0.3125rem', // 5px
    md: '0.4375rem',   // 7px
    lg: '0.625rem',    // 10px
    xl: '0.875rem',    // 14px
    '2xl': '1.125rem', // 18px
    '3xl': '1.5rem',   // 24px
    full: '9999px',
  },

  // 6. Shadows - Stronger, more defined shadows for clarity
  shadows: {
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    base: '0 2px 4px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.08)',
    md: '0 4px 8px -1px rgba(0, 0, 0, 0.12), 0 2px 6px -1px rgba(0, 0, 0, 0.08)',
    lg: '0 12px 20px -4px rgba(0, 0, 0, 0.15), 0 6px 10px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 24px 32px -6px rgba(0, 0, 0, 0.16), 0 12px 16px -6px rgba(0, 0, 0, 0.1)',
    '2xl': '0 32px 60px -12px rgba(0, 0, 0, 0.2)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.08)',
    outline: '0 0 0 3px rgba(41, 171, 226, 0.55)',
    none: 'none',
    // Chat bubble shadows
    chat: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
    chatHover: '0 4px 8px 0 rgba(0, 0, 0, 0.12)',
    // Dark mode shadows
    'dark-sm': '0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    'dark-md': '0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3)',
    'dark-lg': '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.4)',
  },

  // 7. Z-Index Scale
  zIndices: {
    hide: -1,
    auto: 'auto',
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
  },

  // 6. Component Styles
  components: {
    Card,
    Button,
    Table,
    Modal,
    Input,
    Select,
    AlertDialog,
    Tabs,
    Textarea,
    Checkbox,
    Popover,
    Radio,
    Progress,
  },

  // 8. Global Styles
  styles: {
    global: (props) => ({
      '*': {
        boxSizing: 'border-box',
      },
      '*, *::before, *::after': {
        borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.300',
      },
      body: {
        bg: props.colorMode === 'dark' ? 'gray.900' : '#F3FAFD',
        color: props.colorMode === 'dark' ? 'text.inverted' : '#1A202C',
        fontFeatureSettings: '"kern" 1',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        scrollBehavior: 'smooth',
        lineHeight: '1.6',
        letterSpacing: '-0.01em',
      },
      html: {
        scrollBehavior: 'smooth',
      },
      '#root': {
        minHeight: '100vh',
        isolation: 'isolate',
      },
      // Focus styles
      '*:focus': {
        outline: 'none',
        boxShadow: props.colorMode === 'dark'
          ? '0 0 0 2px rgba(255, 183, 0, 0.6)'
          : '0 0 0 2px rgba(41, 171, 226, 0.55)',
      },
      // Hide scrollbars globally (no-scroll layout; content via pagination/collapse)
      '::-webkit-scrollbar': {
        width: 0,
        height: 0,
      },
      scrollbarWidth: 'none',
    }),
  },

  // 9. Color Mode Configuration
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
});

export default theme;
